import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, share, take } from 'rxjs';
import { ServiceBase } from './servicebase.service';
import { MessageService } from 'primeng/api';

@Injectable({
  providedIn: 'root'
})
export class LookupService extends ServiceBase {
  usStatesSubj: BehaviorSubject<ServerModels.ENESS_REF_State[]> = new BehaviorSubject([]);
  usStates$: Observable<ServerModels.ENESS_REF_State[]> = this.usStatesSubj.asObservable();

  gendersSubj: BehaviorSubject<ServerModels.ENESS_REF_Genders[]> = new BehaviorSubject([]);
  genders$: Observable<ServerModels.ENESS_REF_Genders[]> = this.gendersSubj.asObservable();

  racesSubj: BehaviorSubject<ServerModels.ENESS_REF_Races[]> = new BehaviorSubject([]);
  races$: Observable<ServerModels.ENESS_REF_Races[]> = this.racesSubj.asObservable();

  socialMediaTypesSubj: BehaviorSubject<ServerModels.ENESS_REF_SocialMediaTypes[]> = new BehaviorSubject([]);
  socialMediaTypes$: Observable<ServerModels.ENESS_REF_SocialMediaTypes[]> = this.socialMediaTypesSubj.asObservable();

  phoneNumberTypesSubj: BehaviorSubject<ServerModels.ENESS_REF_PhoneNumberTypes[]> = new BehaviorSubject([]);
  phoneNumberTypes$: Observable<ServerModels.ENESS_REF_PhoneNumberTypes[]> = this.phoneNumberTypesSubj.asObservable();

  nibinSitesSubj: BehaviorSubject<ServerModels.NibinSiteViewModel[]> = new BehaviorSubject([]);
  nibinSites$: Observable<ServerModels.NibinSiteViewModel[]> = this.nibinSitesSubj.asObservable();

  fdGroupsSubj: BehaviorSubject<ServerModels.FieldDivisionGroupViewModel[]> = new BehaviorSubject([]);
  fdGroups$: Observable<ServerModels.FieldDivisionGroupViewModel[]> = this.fdGroupsSubj.asObservable();

  countiesSubj: BehaviorSubject<ServerModels.ENESS_REF_Counties[]> = new BehaviorSubject([]);
  counties$: Observable<ServerModels.ENESS_REF_Counties[]> = this.countiesSubj.asObservable();

  triageTierLevelsSubj: BehaviorSubject<ServerModels.ENESS_REF_TriageTierLevel[]> = new BehaviorSubject([]);
 
  constructor(protected http: HttpClient,
    public messageService: MessageService) {
    super(http, messageService);
    this.getUSStates();
    this.getAllGenders();
    this.getAllRaces();
    this.getAllSocialMediaTypes();
    this.getPhoneNumberTypes();
    this.getAllNibinSites();
    this.getAllFDGroups();
    this.getAllCounties();

    if (!this.triageTierLevelsSubj.observed) {
      this.getTriageTierLevels();
    }
  }

  public getAlertTypeID(alertType: string): Observable<string> {
    let options = {
      headers: this.httpOptions.headers,
      params: {
        'alertType': alertType
      }
    };

    return this.http.get<string>(`${this.apiRoot}/api/Alerts/GetAlertTypeID`, options)
      .pipe(catchError(this.swallowError<any>('getAlertTypeID', 'defaultMsg', null, null))); 
  }

  public getAllCrimeTypes(crimeTypeId?: string): Observable<ServerModels.ENESS_REF_CrimeTypes[]> {
    let options = {
      headers: this.httpOptions.headers,
      params: {}
    };

    if (!!crimeTypeId) {
      options.params['crimeTypeId'] = crimeTypeId;
    }
    return this.http.get<ServerModels.ENESS_REF_CrimeTypes[]>(`${this.apiRoot}/api/CrimeType/AllCrimeTypes`, options)
      .pipe(catchError(this.swallowError<any>('getAllCrimeTypes', 'defaultMsg', null, null))); 
  }

  public getDropdownCrimeTypes(): Observable<ServerModels.ENESS_REF_CrimeTypes[]> {
    return this.http.get<ServerModels.ENESS_REF_CrimeTypes[]>(`${this.apiRoot}/api/CrimeType/DropdownCrimeTypes`, this.httpOptions)
      .pipe(catchError(this.swallowError<any>('getDropdownCrimeTypes', 'defaultMsg', null, null))); 
  }

  public getAllGenders() {
    this.http.get<ServerModels.ENESS_REF_Genders[]>(`${this.apiRoot}/api/Genders/AllGenders`, this.httpOptions).pipe(
      take(1),
      catchError(this.swallowError<any>('getAllGenders', 'defaultMsg', null, null))
    ).subscribe((genders: ServerModels.ENESS_REF_Genders[]) => this.gendersSubj.next(genders));
  }

  public getAllRaces() {
    this.http.get<ServerModels.ENESS_REF_Races[]>(`${this.apiRoot}/api/Races/AllRaces`, this.httpOptions).pipe(
      take(1),
      catchError(this.swallowError<any>('getAllRaces', 'defaultMsg', null, null))
    ).subscribe((races: ServerModels.ENESS_REF_Races[]) => this.racesSubj.next(races));
  }

  public getUSStates() {
    this.http.get<ServerModels.ENESS_REF_State[]>(`${this.apiRoot}/api/State/GetUSStates`, this.httpOptions).pipe(
      take(1),
      catchError(this.swallowError<any>('getUSStates', 'defaultMsg', null, null))
    ).subscribe((results: ServerModels.ENESS_REF_State[]) => this.usStatesSubj.next(results));
  }

  public getAllStatuses(): Observable<ServerModels.ENESS_REF_Statuses[]> {
    return this.http.get<ServerModels.ENESS_REF_Statuses[]>(`${this.apiRoot}/api/Statuses/AllStatuses`, this.httpOptions).pipe(
      share(),
      catchError(this.swallowError<any>('getAllStatuses', 'defaultMsg', null, null))
    );
  }

  public getAllSocialMediaTypes() {
    this.http.get<ServerModels.ENESS_REF_SocialMediaTypes[]>(`${this.apiRoot}/api/SocialMedia/AllSocialMediaTypes`, this.httpOptions).pipe(
      take(1),
      catchError(this.swallowError<any>('getAllSocialMediaTypes', 'defaultMsg', null, null))
    ).subscribe((smTypes: ServerModels.ENESS_REF_SocialMediaTypes[]) => this.socialMediaTypesSubj.next(smTypes));
  }

  public getAllTimeZones(): Observable<ServerModels.ENESS_REF_TimeZones[]> {
    return this.http.get<ServerModels.ENESS_REF_TimeZones[]>(`${this.apiRoot}/api/TimeZone/AllTimeZones`, this.httpOptions)
      .pipe(catchError(this.swallowError<any>('getAllTimeZones', 'defaultMsg', null, null))); 
  }

  public getCountiesByFieldDivision(fieldDivisionId: string): Observable<ServerModels.ENESS_REF_Counties[]> {
    let options = {
      headers: this.httpOptions.headers,
      params: {
        'fieldDivisionId': fieldDivisionId
      }
    };

    return this.http.get<ServerModels.ENESS_REF_Counties[]>(`${this.apiRoot}/api/State/CountiesForFieldDivision`, options)
      .pipe(catchError(this.swallowError<any>('getCountiesByFieldDivision', 'defaultMsg', null, null))); 
  }

  public getCitiesByState(stateId: string): Observable<ServerModels.ENESS_REF_Cities[]> {
    let options = {
      headers: this.httpOptions.headers,
      params: {
        'stateId': stateId
      }
    };
    return this.http.get<ServerModels.ENESS_REF_Cities[]>(`${this.apiRoot}/api/Cities/CitiesByState`, options)
      .pipe(catchError(this.swallowError<any>('getCitiesByState', 'defaultMsg', null, null))); 
  }

  public searchCounties(query: string): Observable<ServerModels.ENESS_REF_Counties[]> {
    let options = {
      headers: this.httpOptions.headers,
      params: {
        'query': query
      }
    };

    return this.http.get<ServerModels.ENESS_REF_Counties[]>(`${this.apiRoot}/api/State/SearchCounties`, options)
      .pipe(catchError(this.swallowError<any>('searchCounties', 'defaultMsg', null, null))); 
  }

  public getCountiesByState(stateId: string): Observable<ServerModels.ENESS_REF_Counties[]> {
    let options = {
      headers: this.httpOptions.headers,
      params: {
        'stateId': stateId
      }
    };
    return this.http.get<ServerModels.ENESS_REF_Counties[]>(`${this.apiRoot}/api/State/CountiesForState`, options)
      .pipe(catchError(this.swallowError<any>('getCountiesByState', 'defaultMsg', null, null))); 
  }

  public getStatesByFieldDivision(fieldDivisionId: string): Observable<ServerModels.ENESS_REF_State[]> {
    let options = {
      headers: this.httpOptions.headers,
      params: {
        'fieldDivisionId': fieldDivisionId
      }
    };

    return this.http.get<ServerModels.ENESS_REF_State[]>(`${this.apiRoot}/api/State/StatesForFieldDivision`, options)
      .pipe(catchError(this.swallowError<any>('getStatesByFieldDivision', 'defaultMsg', null, null))); 
  }

  public getTriageTierLevels(): void {
    this.http.get<ServerModels.ENESS_REF_TriageTierLevel[]>(`${this.apiRoot}/api/CrimeGun/GetTriageTiers`, this.httpOptions)
      .pipe(catchError(this.swallowError<any>('getTriageTierLevels', 'defaultMsg', null, null)))
      .subscribe((triageLevels: ServerModels.ENESS_REF_TriageTierLevel[]) => this.triageTierLevelsSubj.next(triageLevels)); 
  }

  public getPhoneNumberTypes() {
    this.http.get<ServerModels.ENESS_REF_PhoneNumberTypesDTO[]>(`${this.apiRoot}/api/PhoneNumbers/GetPhoneNumberTypes`, this.httpOptions).pipe(
      take(1),
      catchError(this.swallowError<any>('getPhoneNumberTypes', 'defaultMsg', null, null))
    ).subscribe((phTypes: ServerModels.ENESS_REF_PhoneNumberTypesDTO[]) => this.phoneNumberTypesSubj.next(phTypes));
  }

  public getAllNibinSites() {
    this.http.get<ServerModels.NibinSiteViewModel[]>(`${this.apiRoot}/api/Search/AllNIBINSites`, this.httpOptions).pipe(
      take(1),
      catchError(this.swallowError<any>('gegetAllNibinSitestAllFDGroups', 'defaultMsg', null, null))
    ).subscribe((sites: ServerModels.NibinSiteViewModel[]) => this.nibinSitesSubj.next(sites));
  }

  public getAllFDGroups() {
    this.http.get<ServerModels.FieldDivisionGroupViewModel[]>(`${this.apiRoot}/api/Search/GetAllFieldDivisionGroups`, this.httpOptions).pipe(
      take(1),
      catchError(this.swallowError<any>('getAllFDGroups', 'defaultMsg', null, null))
    ).subscribe((sites: ServerModels.FieldDivisionGroupViewModel[]) => this.fdGroupsSubj.next(sites));
  }

  public getFDGroupByFieldDivision(fieldDivisionId: string): Observable<ServerModels.FieldDivisionGroupViewModel[]> {
    let options = {
      headers: this.httpOptions.headers,
      params: {
        'fieldDivisionId': fieldDivisionId
      }
    };

    return this.http.get<ServerModels.FieldDivisionGroupViewModel[]>(`${this.apiRoot}/api/Search/GetFieldDivisionGroupsByFDID`, options)
      .pipe(catchError(this.swallowError<any>('getFDGroupByFieldDivision', 'defaultMsg', null, null))); 
  }

  public getAgenciesByFieldDivisionId(fieldDivisionId: string): Observable<ServerModels.AgencyViewModel[]> {
    let options = {
      headers: this.httpOptions.headers,
      params: {
        'fielddivisionId': fieldDivisionId
      }
    };

    return this.http.get<ServerModels.FieldDivisionGroupViewModel[]>(`${this.apiRoot}/api/Agency/AgenciesByFieldDivisionId`, options)
      .pipe(catchError(this.swallowError<any>('getAgenciesByFieldDivisionId', 'defaultMsg', null, null))); 
  }

  public getAgenciesByCountyIds(countyIds: string): Observable<ServerModels.AgencyViewModel[]> {
    return this.http.put<ServerModels.FieldDivisionGroupViewModel[]>(`${this.apiRoot}/api/Agency/GetAgenciesByCountyIds`, { 'countyIds': countyIds }, this.httpOptions)
      .pipe(catchError(this.swallowError<any>('getAgenciesByCountyIds', 'defaultMsg', null, null))); 
  }

  public getAgenciesByFieldDivisionGroupIds(fieldDivisionGroupIds: string): Observable<ServerModels.AgencyViewModel[]> {
    return this.http.put<ServerModels.AgencyViewModel[]>(`${this.apiRoot}/api/Agency/AgenciesByFieldDivisionGroupIds`, { 'fieldDivisionGroupIds': fieldDivisionGroupIds }, this.httpOptions)
      .pipe(catchError(this.swallowError<any>('getAgenciesByFieldDivisionGroupIds', 'defaultMsg', null, null))); 
  }
  
  public getStatesForFieldDivisionGroups(fieldDivisionGroupIds: string): Observable<ServerModels.AgencyFieldDivisionGroupViewModel[]> {
    return this.http.put<ServerModels.AgencyFieldDivisionGroupViewModel[]>(`${this.apiRoot}/api/State/GetStatesForFieldDivisionGroups`, { 'fieldDivisionGroupIds': fieldDivisionGroupIds }, this.httpOptions)
      .pipe(catchError(this.swallowError<any>('getStatesForFieldDivisionGroups', 'defaultMsg', null, null))); 
  }

  public getCountiesForFieldDivisionGroup(fieldDivisionGroupIds: string): Observable<ServerModels.ENESS_REF_Counties[]> {
    return this.http.put<ServerModels.ENESS_REF_Counties[]>(`${this.apiRoot}/api/State/CountiesForFieldDivisionGroup`, { 'fieldDivisionGroupIds': fieldDivisionGroupIds }, this.httpOptions)
      .pipe(catchError(this.swallowError<any>('getCountiesForFieldDivisionGroup', 'defaultMsg', null, null))); 
  }

  public getCountiesForFDGroupAndState(fieldDivisionGroupIds: string, stateId: string): Observable<ServerModels.ENESS_REF_Counties[]> {
    return this.http.put<ServerModels.ENESS_REF_Counties[]>(`${this.apiRoot}/api/State/CountiesForFDGroupAndState`, { 'fieldDivisionGroupIds': fieldDivisionGroupIds, 'stateId': stateId }, this.httpOptions)
      .pipe(catchError(this.swallowError<any>('getCountiesForFDGroupAndState', 'defaultMsg', null, null))); 
  }

  public getAgenciesByFDGroupStateCounty(fdGroup: string, stateId: string, countyIds: string): Observable<ServerModels.AgencyViewModel[]> {
    return this.http.put<ServerModels.AgencyViewModel[]>(`${this.apiRoot}/api/Agency/GetAgenciesByFDGroupStateCounty`, { 'fdGroup': fdGroup, 'stateId': stateId, 'countyIds': countyIds }, this.httpOptions)
      .pipe(catchError(this.swallowError<any>('getAgenciesByFDGroupStateCounty', 'defaultMsg', null, null))); 
  }

  public getAllCounties() {
    this.http.get<ServerModels.ENESS_REF_Counties[]>(`${this.apiRoot}/api/Agency/GetAllCounties`, this.httpOptions).pipe(
      take(1),
      catchError(this.swallowError<any>('getAllCounties', 'defaultMsg', null, null)) 
    ).subscribe((counties: ServerModels.ENESS_REF_Counties[]) => this.countiesSubj.next(counties));
  }
  
}
